import React, { useEffect, useState } from "react";
import axios from "axios";
import { TrendingUp } from "lucide-react";
import { adminAuthHeader } from "@/hooks/useAdminAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtEur = (cents) => ((cents || 0) / 100).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDay = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
};

/**
 * Mini-grafico SVG dei ricavi giornalieri degli ultimi `days` giorni.
 * Polylinea anti-aliased + area sotto. Niente librerie esterne.
 */
export default function RevenueSparkline({ days = 30, perCustomer = [] }) {
  const [data, setData] = useState({ series: [], max_cents: 0 });
  const [loading, setLoading] = useState(true);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/admin/revenue/timeseries?days=${days}`, { headers: adminAuthHeader() })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { /* ignore */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const { series = [], max_cents = 0 } = data;
  const W = 720;
  const H = 110;
  const PAD = { l: 8, r: 8, t: 10, b: 18 };
  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;
  const maxY = Math.max(max_cents, 100); // floor 1€ to avoid /0
  const stepX = series.length > 1 ? innerW / (series.length - 1) : 0;
  const pts = series.map((p, i) => ({
    x: PAD.l + i * stepX,
    y: PAD.t + innerH * (1 - (p.cents || 0) / maxY),
    date: p.date,
    cents: p.cents || 0,
  }));
  const polyline = pts.map(p => `${p.x},${p.y}`).join(" ");
  const area = pts.length
    ? `${PAD.l},${PAD.t + innerH} ${polyline} ${pts[pts.length - 1].x},${PAD.t + innerH}`
    : "";

  const totalCents = series.reduce((s, p) => s + (p.cents || 0), 0);
  const top5 = (perCustomer || []).slice(0, 5);

  return (
    <section data-testid="revenue-sparkline" className="bg-white rounded-2xl border border-[#e3eaf2] mb-6 overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr]">
        {/* Sparkline */}
        <div className="p-5 border-b lg:border-b-0 lg:border-r border-[#eef2f7]">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <p className="text-[11px] tracking-[0.18em] uppercase flex items-center gap-1.5" style={{ color: "var(--np-muted)" }}>
                <TrendingUp size={13} /> Andamento ultimi {days} giorni
              </p>
              <p className="mt-1 text-2xl font-semibold" style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}>
                € {fmtEur(totalCents)}
              </p>
            </div>
            <p className="text-[11px] text-right" style={{ color: "var(--np-muted)" }}>
              Max giornaliero<br/><span className="font-semibold" style={{ color: "var(--np-primary)" }}>€ {fmtEur(maxY === 100 ? 0 : maxY)}</span>
            </p>
          </div>
          {loading || pts.length === 0 ? (
            <div className="h-[110px] flex items-center justify-center text-xs" style={{ color: "var(--np-muted)" }}>
              {loading ? "Caricamento…" : "Nessun ricavo nei giorni selezionati"}
            </div>
          ) : (
            <div className="relative">
              <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[110px]" preserveAspectRatio="none">
                <polygon points={area} fill="var(--np-secondary)" fillOpacity="0.12" />
                <polyline points={polyline} fill="none" stroke="var(--np-secondary)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                {pts.map((p) => (
                  <g key={p.date}>
                    <circle cx={p.x} cy={p.y} r={p.cents > 0 ? 3 : 1.5} fill="var(--np-secondary)" />
                    <rect x={p.x - stepX / 2} y={0} width={stepX || 10} height={H} fill="transparent"
                      onMouseEnter={() => setHover(p)} onMouseLeave={() => setHover(null)} />
                  </g>
                ))}
                {pts.length > 0 && (
                  <>
                    <text x={pts[0].x} y={H - 4} fontSize="9" fill="var(--np-muted)" textAnchor="start">{fmtDay(pts[0].date)}</text>
                    <text x={pts[pts.length - 1].x} y={H - 4} fontSize="9" fill="var(--np-muted)" textAnchor="end">{fmtDay(pts[pts.length - 1].date)}</text>
                  </>
                )}
              </svg>
              {hover && (
                <div className="absolute pointer-events-none px-2 py-1 rounded-md text-[11px] font-semibold"
                  style={{ background: "var(--np-primary)", color: "#fff", left: `${(hover.x / W) * 100}%`, top: 4, transform: "translateX(-50%)" }}>
                  {fmtDay(hover.date)} · € {fmtEur(hover.cents)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Top customers */}
        <div className="p-5">
          <p className="text-[11px] tracking-[0.18em] uppercase mb-3" style={{ color: "var(--np-muted)" }}>
            Top 5 clienti per spesa
          </p>
          {top5.length === 0 ? (
            <p className="text-xs" style={{ color: "var(--np-muted)" }}>Nessun acquisto ancora.</p>
          ) : (
            <ol className="space-y-2.5" data-testid="top-customers">
              {top5.map((c, i) => (
                <li key={c.user_id} className="flex items-center gap-3 cursor-pointer hover:bg-[#fafcfe] -mx-2 px-2 py-1 rounded-md"
                  onClick={() => { window.location.href = `/admin/utente/${c.user_id}`; }}>
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0"
                    style={{ background: i === 0 ? "var(--np-secondary)" : "#f0f4f8", color: i === 0 ? "#fff" : "var(--np-primary)" }}>
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] truncate" style={{ color: "var(--np-primary)" }}>{c.email || c.user_id.slice(0, 8)}</p>
                    <p className="text-[10px]" style={{ color: "var(--np-muted)" }}>{c.count} acquist{c.count === 1 ? "o" : "i"}</p>
                  </div>
                  <span className="text-sm font-bold whitespace-nowrap" style={{ color: "var(--np-secondary)" }}>€ {fmtEur(c.total_cents)}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </section>
  );
}
